import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { Unit } from "@/graphql"
import { UseRootStore } from "@/model/RootStore"
import { AreaUnit, convertArea, convertDistance, convertPricePerArea, DistanceUnit } from "@/utils/unitSystem"

export function useUnitSystem() {
	const store = UseRootStore()
	const { t } = useI18n()
	const currentUserDetails = computed(() => store.CurrentUserBasicDetails ?? store.CurrentUserDetails)
	const unit = computed(() => currentUserDetails.value?.unit ?? Unit.Imperial)
	const areaUnit = computed(() => (unit.value === Unit.Metric ? AreaUnit.SquareMeters : AreaUnit.SquareFeet))
	const distanceUnit = computed(() => (unit.value === Unit.Metric ? DistanceUnit.Meters : DistanceUnit.Feet))
	const areaUnitSymbol = computed(() => t(`unitSystem.area.${areaUnit.value}`))
	const distanceUnitSymbol = computed(() => t(`unitSystem.distance.${distanceUnit.value}`))
	const pricePerAreaUnitSymbol = computed(() => t(`unitSystem.pricePerArea.${areaUnit.value}`))

	const convertAreaToPreferredUnit = (value: number, sourceUnit: AreaUnit) => {
		return convertArea(value, sourceUnit, areaUnit.value)
	}

	const convertAreaFromPreferredUnit = (value: number, targetUnit: AreaUnit) => {
		return convertArea(value, areaUnit.value, targetUnit)
	}

	const convertDistanceToPreferredUnit = (value: number, sourceUnit: DistanceUnit) => {
		return convertDistance(value, sourceUnit, distanceUnit.value)
	}

	const convertDistanceFromPreferredUnit = (value: number, targetUnit: DistanceUnit) => {
		return convertDistance(value, distanceUnit.value, targetUnit)
	}

	const convertPricePerAreaToPreferredUnit = (value: number, sourceUnit: AreaUnit) => {
		return convertPricePerArea(value, sourceUnit, areaUnit.value)
	}

	const convertPricePerAreaFromPreferredUnit = (value: number, targetUnit: AreaUnit) => {
		return convertPricePerArea(value, areaUnit.value, targetUnit)
	}

	return {
		unit,
		areaUnit,
		distanceUnit,
		areaUnitSymbol,
		distanceUnitSymbol,
		pricePerAreaUnitSymbol,
		convertAreaToPreferredUnit,
		convertAreaFromPreferredUnit,
		convertDistanceToPreferredUnit,
		convertDistanceFromPreferredUnit,
		convertPricePerAreaToPreferredUnit,
		convertPricePerAreaFromPreferredUnit,
	}
}
